import React, { useState } from 'react';
import axios from 'axios';
import { Heart, ShieldCheck, Loader2, Sparkles, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESETS = [25, 50, 100, 250, 500];

const DonationSection = () => {
  const { user, login } = useAuth();
  const [selected, setSelected] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const effectiveAmount = customAmount !== '' ? parseFloat(customAmount) : selected;
  const validAmount = !!effectiveAmount && !isNaN(effectiveAmount) && effectiveAmount >= 1;

  const handlePreset = (amt) => {
    setSelected(amt);
    setCustomAmount('');
    setError('');
  };

  const handleCustom = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setCustomAmount(val);
      setSelected(null);
      setError('');
    }
  };

  const handleDonate = async () => {
    if (!user) {
      login();
      return;
    }
    if (!agreed) {
      setError('Please confirm you are aware of the amount you are donating.');
      return;
    }
    const amount = effectiveAmount;
    if (!amount || isNaN(amount) || amount < 1) {
      setError('Please enter a valid amount of at least $1.');
      return;
    }
    if (amount > 50000) {
      setError('The maximum donation amount is $50,000.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/donations/checkout`, {
        amount,
        origin_url: window.location.origin,
        donor_name: donorName || undefined,
      }, { withCredentials: true });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Could not start the donation. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Donation checkout failed:', err);
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="section-padding" style={{
      background: 'linear-gradient(180deg, #1a0e2e 0%, #0f0a1e 100%)',
    }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)',
            borderRadius: 999, padding: '6px 18px',
          }}>
            <Sparkles size={15} color="#00d4ff" />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#00d4ff', letterSpacing: 1, textTransform: 'uppercase' }}>
              Support Our Mission
            </span>
          </div>
        </div>

        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(32px, 4vw, 48px)',
          fontWeight: 700,
          marginBottom: '10px',
        }}>
          Give the Gift of <span style={{ color: '#00d4ff' }}>Consciousness</span>
        </h2>
        <p dir="rtl" style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#e0dff0',
          fontWeight: 600,
          marginBottom: '6px',
          lineHeight: 1.8,
        }}>
          ادعم رسالتنا في نشر الوعي والمعرفة الروحانية
        </p>
        <p style={{
          textAlign: 'center',
          fontSize: '15px',
          color: '#b0adc0',
          marginBottom: '14px',
        }}>
          Your generosity fuels courses, books, and free wisdom for seekers around the world.
        </p>
        <div className="section-divider" style={{ marginBottom: '40px' }} />

        <div className="glass-card" style={{ padding: 'clamp(24px, 5vw, 44px)' }}>
          {/* Preset amounts */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#8885a0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14, textAlign: 'center' }}>
            Choose an amount (USD)
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
            gap: '12px',
            marginBottom: '22px',
          }}>
            {PRESETS.map((amt) => {
              const active = selected === amt && customAmount === '';
              return (
                <button
                  key={amt}
                  data-testid={`donation-preset-${amt}`}
                  onClick={() => handlePreset(amt)}
                  style={{
                    padding: '16px 8px',
                    borderRadius: 14,
                    fontSize: 20,
                    fontWeight: 700,
                    fontFamily: 'Inter, sans-serif',
                    cursor: 'pointer',
                    color: active ? '#0f0a1e' : '#e0dff0',
                    background: active ? 'linear-gradient(135deg, #00d4ff 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.04)',
                    border: active ? '1px solid transparent' : '1px solid rgba(139,92,246,0.25)',
                    transition: 'transform 0.2s ease, background 0.2s ease, border-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = 'rgba(0,212,255,0.5)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = 'rgba(139,92,246,0.25)'; }}
                >
                  ${amt}
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div style={{ marginBottom: '22px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#8885a0', marginBottom: 8 }}>
              Or enter a custom amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', fontSize: 22, fontWeight: 700, color: '#00d4ff' }}>$</span>
              <input
                type="text"
                inputMode="decimal"
                data-testid="donation-custom-amount"
                value={customAmount}
                onChange={handleCustom}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  padding: '16px 18px 16px 42px',
                  borderRadius: 14,
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#ffffff',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(139,92,246,0.25)',
                  outline: 'none',
                  fontFamily: 'Inter, sans-serif',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#00d4ff'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.25)'; }}
              />
            </div>
          </div>

          {/* Donor name (optional) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#8885a0', marginBottom: 8 }}>
              Your name <span style={{ color: '#6b6880', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              data-testid="donation-donor-name"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              placeholder="Anonymous"
              style={{
                width: '100%',
                padding: '14px 18px',
                borderRadius: 14,
                fontSize: 15,
                color: '#ffffff',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(139,92,246,0.25)',
                outline: 'none',
                fontFamily: 'Inter, sans-serif',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#00d4ff'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.25)'; }}
            />
          </div>

          {/* Amount-awareness checkbox */}
          <label data-testid="donation-agree-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, cursor: 'pointer', textAlign: 'left' }}>
            <input
              type="checkbox"
              data-testid="donation-agree-checkbox"
              checked={agreed}
              onChange={(e) => { setAgreed(e.target.checked); setError(''); }}
              style={{ width: 18, height: 18, marginTop: 2, accentColor: '#00d4ff', cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: '#b0adc0', lineHeight: 1.6 }}>
              I understand and confirm that I am donating{' '}
              <strong style={{ color: '#00d4ff' }}>{validAmount ? `$${Number(effectiveAmount).toLocaleString()}` : 'the amount selected above'}</strong>{' '}
              (USD), and that this one-time payment is non-refundable.
            </span>
          </label>

          {error && (
            <p data-testid="donation-error" style={{ color: '#ef4444', fontSize: 14, textAlign: 'center', marginBottom: 16 }}>
              {error}
            </p>
          )}

          {/* Donate / Sign-in button */}
          {user ? (
            <button
              className="btn-primary-gradient"
              data-testid="donate-submit-btn"
              onClick={handleDonate}
              disabled={loading || !agreed}
              style={{
                width: '100%',
                padding: '18px',
                fontSize: 18,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                cursor: loading ? 'wait' : (!agreed ? 'not-allowed' : 'pointer'),
                opacity: loading ? 0.8 : (!agreed ? 0.55 : 1),
              }}
            >
              {loading ? (
                <><Loader2 size={20} className="spin-anim" /> Redirecting to secure checkout…</>
              ) : (
                <><Heart size={20} fill="#0f0a1e" /> Donate {validAmount ? `$${Number(effectiveAmount).toLocaleString()}` : ''}</>
              )}
            </button>
          ) : (
            <button
              className="btn-primary-gradient"
              data-testid="donate-signin-btn"
              onClick={login}
              style={{
                width: '100%',
                padding: '18px',
                fontSize: 17,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                cursor: 'pointer',
              }}
            >
              <LogIn size={20} /> Sign in to Donate
            </button>
          )}

          {!user && (
            <p data-testid="donation-signin-hint" style={{ fontSize: 12, color: '#8885a0', textAlign: 'center', marginTop: 12 }}>
              You must be signed in to make a donation.
            </p>
          )}

          {/* Trust note */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 18 }}>
            <ShieldCheck size={15} color="#8885a0" />
            <span style={{ fontSize: 12, color: '#8885a0' }}>
              Secure one-time payment via Stripe. Donations are non-refundable.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;
